import { useState, useEffect } from "react";
import {
  Settings,
  Database,
  Upload,
  Download,
  Plus,
  Trash2,
  BarChart3,
  Users,
  Activity,
  FileText,
  Calendar,
  Clock,
} from "lucide-react";
import medicalDB from "../services/database";
import DraftManager from "./DraftManager";

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalPatients: 0,
    underObservation: 0,
    followUp: 0,
    discharged: 0,
    totalVisits: 0,
    totalAppointments: 0,
    todaysAppointments: 0,
    upcomingAppointments: 0,
    completedAppointments: 0,
  });
  const [medicalDatasets, setMedicalDatasets] = useState({
    symptoms: [],
    drugs: [],
    allergens: [],
    diagnoses: [],
    routes: [],
    "administered-medications": [],
  });
  const [newItem, setNewItem] = useState({ category: "symptoms", name: "" });
  const [loading, setLoading] = useState(true);
  const [isAgentVisible, setIsAgentVisible] = useState(true);

  useEffect(() => {
    loadDashboardData();
    // Load agent visibility preference
    const savedAgentVisibility = localStorage.getItem("agentVisible");
    if (savedAgentVisibility !== null) {
      setIsAgentVisible(savedAgentVisibility === "true");
    }
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      // Load patient statistics
      const patients = await medicalDB.getAllPatients();

      // Load appointment statistics
      const allAppointments = await medicalDB.getAllAppointments();
      const todaysAppointments = await medicalDB.getTodaysAppointments();

      // Calculate upcoming appointments (future scheduled appointments)
      const today = new Date();
      const upcomingAppointments = allAppointments.filter((apt) => {
        const aptDate = new Date(apt.date);
        return aptDate > today && apt.status === "scheduled";
      });

      // Calculate completed appointments
      const completedAppointments = allAppointments.filter(
        (apt) => apt.status === "completed"
      );

      const statsData = {
        totalPatients: patients.length,
        underObservation: patients.filter(
          (p) => p.status === "Under Observation"
        ).length,
        followUp: patients.filter((p) => p.status === "Follow-Up").length,
        discharged: patients.filter((p) => p.status === "Discharged").length,
        totalVisits: patients.reduce(
          (total, patient) => total + (patient.visitCount || 0),
          0
        ),
        totalAppointments: allAppointments.length,
        todaysAppointments: todaysAppointments.length,
        upcomingAppointments: upcomingAppointments.length,
        completedAppointments: completedAppointments.length,
      };
      setStats(statsData);

      // Load medical datasets
      const [symptoms, drugs, allergens, diagnoses, routes, administeredMeds] =
        await Promise.all([
          medicalDB.getMedicalDataset("symptoms"),
          medicalDB.getMedicalDataset("drugs"),
          medicalDB.getMedicalDataset("allergens"),
          medicalDB.getMedicalDataset("diagnoses"),
          medicalDB.getMedicalDataset("routes"),
          medicalDB.getMedicalDataset("administered-medications"),
        ]);

      setMedicalDatasets({
        symptoms: symptoms || [],
        drugs: drugs || [],
        allergens: allergens || [],
        diagnoses: diagnoses || [],
        routes: routes || [],
        "administered-medications": administeredMeds || [],
      });
    } catch (error) {
      console.error("Error loading dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    if (!newItem.name.trim()) return;

    try {
      await medicalDB.addMedicalDatasetItem(
        newItem.category,
        newItem.name.trim()
      );
      setNewItem({ ...newItem, name: "" });
      loadDashboardData(); // Reload data
    } catch (error) {
      console.error("Error adding item:", error);
      alert("Failed to add item. It may already exist.");
    }
  };

  const handleDeleteItem = async (itemId, category) => {
    if (!confirm("Are you sure you want to delete this item?")) return;

    try {
      await medicalDB.deleteMedicalDatasetItem(itemId);
      loadDashboardData(); // Reload data
    } catch (error) {
      console.error("Error deleting item:", error);
      alert("Failed to delete item.");
    }
  };

  const handleExportData = async () => {
    try {
      const data = await medicalDB.exportData();
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `medical-records-backup-${
        new Date().toISOString().split("T")[0]
      }.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export failed:", error);
      alert("Export failed. Please try again.");
    }
  };

  const handleImportData = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const text = await file.text();
      const data = JSON.parse(text);

      if (confirm("This will import data from the file. Continue?")) {
        await medicalDB.importData(data);
        loadDashboardData(); // Reload data
        alert("Data imported successfully!");
      }
    } catch (error) {
      console.error("Import failed:", error);
      alert("Import failed. Please check the file format.");
    }

    // Reset file input
    event.target.value = "";
  };

  const handleAgentVisibilityToggle = () => {
    const newVisibility = !isAgentVisible;
    setIsAgentVisible(newVisibility);
    localStorage.setItem("agentVisible", newVisibility.toString());

    // Dispatch custom event to notify FloatingAgent component
    window.dispatchEvent(
      new CustomEvent("agentVisibilityChanged", {
        detail: { isVisible: newVisibility },
      })
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-3">
        <Settings className="h-8 w-8 text-medical-blue" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600">
            Manage medical datasets and application settings
          </p>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        <div className="card bg-medical-lightblue border-l-4 border-medical-blue">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-medical-blue">
                Total Patients
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {stats.totalPatients}
              </p>
            </div>
            <Users className="h-8 w-8 text-medical-blue" />
          </div>
        </div>

        <div className="card bg-orange-50 border-l-4 border-medical-orange">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-medical-orange">
                Under Observation
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {stats.underObservation}
              </p>
            </div>
            <Activity className="h-8 w-8 text-medical-orange" />
          </div>
        </div>

        <div className="card bg-blue-50 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-600">Follow-Up</p>
              <p className="text-2xl font-bold text-gray-900">
                {stats.followUp}
              </p>
            </div>
            <FileText className="h-8 w-8 text-blue-500" />
          </div>
        </div>

        <div className="card bg-green-50 border-l-4 border-medical-green">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-medical-green">
                Discharged
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {stats.discharged}
              </p>
            </div>
            <BarChart3 className="h-8 w-8 text-medical-green" />
          </div>
        </div>

        <div className="card bg-purple-50 border-l-4 border-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-purple-600">
                Total Visits
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {stats.totalVisits}
              </p>
            </div>
            <Activity className="h-8 w-8 text-purple-500" />
          </div>
        </div>
      </div>

      {/* Appointment Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div
          className="card bg-indigo-50 border-l-4 border-indigo-500 hover:shadow-lg transition-shadow cursor-pointer"
          onClick={() => (window.location.href = "/appointments")}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-indigo-600">
                Total Appointments
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {stats.totalAppointments}
              </p>
              <p className="text-xs text-indigo-500 mt-1">
                View all appointments
              </p>
            </div>
            <Calendar className="h-8 w-8 text-indigo-500" />
          </div>
        </div>

        <div
          className="card bg-yellow-50 border-l-4 border-yellow-500 hover:shadow-lg transition-shadow cursor-pointer"
          onClick={() => (window.location.href = "/appointments/calendar")}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-yellow-600">
                Today's Appointments
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {stats.todaysAppointments}
              </p>
              <p className="text-xs text-yellow-500 mt-1">View calendar</p>
            </div>
            <Clock className="h-8 w-8 text-yellow-500" />
          </div>
        </div>

        <div
          className="card bg-cyan-50 border-l-4 border-cyan-500 hover:shadow-lg transition-shadow cursor-pointer"
          onClick={() => (window.location.href = "/appointments/new")}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-cyan-600">
                Upcoming Appointments
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {stats.upcomingAppointments}
              </p>
              <p className="text-xs text-cyan-500 mt-1">Schedule new</p>
            </div>
            <Calendar className="h-8 w-8 text-cyan-500" />
          </div>
        </div>

        <div
          className="card bg-emerald-50 border-l-4 border-emerald-500 hover:shadow-lg transition-shadow cursor-pointer"
          onClick={() => (window.location.href = "/appointments")}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-emerald-600">
                Completed Appointments
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {stats.completedAppointments}
              </p>
              <p className="text-xs text-emerald-500 mt-1">View history</p>
            </div>
            <BarChart3 className="h-8 w-8 text-emerald-500" />
          </div>
        </div>
      </div>

      {/* Draft Management */}
      <div className="card">
        <DraftManager />
      </div>

      {/* Data Management */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Medical Datasets */}
        <div className="card">
          <div className="flex items-center space-x-3 mb-6">
            <Database className="h-6 w-6 text-medical-blue" />
            <h2 className="text-xl font-semibold text-gray-900">
              Medical Datasets
            </h2>
          </div>

          {/* Add New Item Form */}
          <form
            onSubmit={handleAddItem}
            className="mb-6 p-4 bg-gray-50 rounded-lg"
          >
            <h3 className="font-medium text-gray-900 mb-3">Add New Item</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <select
                value={newItem.category}
                onChange={(e) =>
                  setNewItem({ ...newItem, category: e.target.value })
                }
                className="form-input"
              >
                <option value="symptoms">Symptoms</option>
                <option value="drugs">Drugs</option>
                <option value="allergens">Allergens</option>
                <option value="diagnoses">Diagnoses</option>
                <option value="routes">Routes</option>
                <option value="administered-medications">
                  Administered Medications
                </option>
              </select>

              <input
                type="text"
                value={newItem.name}
                onChange={(e) =>
                  setNewItem({ ...newItem, name: e.target.value })
                }
                placeholder="Enter item name"
                className="form-input"
                required
              />

              <button
                type="submit"
                className="btn-primary flex items-center justify-center space-x-2"
              >
                <Plus size={16} />
                <span>Add</span>
              </button>
            </div>
          </form>

          {/* Dataset Categories */}
          <div className="space-y-4">
            {Object.entries(medicalDatasets).map(([category, items]) => (
              <div key={category}>
                <h4 className="font-medium text-gray-900 mb-2 capitalize flex items-center justify-between">
                  {category}
                  <span className="text-sm text-gray-500">
                    ({items.length} items)
                  </span>
                </h4>
                <div className="max-h-32 overflow-y-auto border border-gray-200 rounded p-2">
                  {items.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {items.map((item) => (
                        <span
                          key={item.id}
                          className={`inline-flex items-center px-2 py-1 rounded text-xs ${
                            item.isCustom
                              ? "bg-medical-lightblue text-medical-blue"
                              : "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {item.name}
                          {item.isCustom && (
                            <button
                              onClick={() =>
                                handleDeleteItem(item.id, category)
                              }
                              className="ml-1 text-red-500 hover:text-red-700"
                            >
                              <Trash2 size={12} />
                            </button>
                          )}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm">
                      No items in this category
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Data Backup & Restore */}
        <div className="card">
          <div className="flex items-center space-x-3 mb-6">
            <Database className="h-6 w-6 text-medical-blue" />
            <h2 className="text-xl font-semibold text-gray-900">
              Data Backup & Restore
            </h2>
          </div>

          <div className="space-y-4">
            {/* Export Data */}
            <div>
              <h3 className="font-medium text-gray-900 mb-2">Export Data</h3>
              <p className="text-sm text-gray-600 mb-3">
                Download all patient records and custom medical dataset entries
                as a JSON file.
              </p>
              <button
                onClick={handleExportData}
                className="btn-primary flex items-center space-x-2"
              >
                <Download size={20} />
                <span>Export All Data</span>
              </button>
            </div>

            {/* Import Data */}
            <div className="pt-4 border-t border-gray-200">
              <h3 className="font-medium text-gray-900 mb-2">Import Data</h3>
              <p className="text-sm text-gray-600 mb-3">
                Import patient records and medical data from a previously
                exported JSON file.
              </p>
              <div>
                <input
                  type="file"
                  accept=".json"
                  onChange={handleImportData}
                  className="hidden"
                  id="import-file"
                />
                <label
                  htmlFor="import-file"
                  className="btn-secondary flex items-center space-x-2 cursor-pointer"
                >
                  <Upload size={20} />
                  <span>Import Data</span>
                </label>
              </div>
            </div>

            {/* Clear Data Warning */}
            <div className="pt-4 border-t border-gray-200">
              <h3 className="font-medium text-red-600 mb-2">Danger Zone</h3>
              <p className="text-sm text-gray-600 mb-3">
                Clear all patient data and custom medical entries. This action
                cannot be undone.
              </p>
              <button
                onClick={() => {
                  if (
                    confirm(
                      "Are you sure you want to clear ALL data? This cannot be undone!"
                    )
                  ) {
                    medicalDB.clearAllData().then(() => {
                      loadDashboardData();
                      alert("All data has been cleared.");
                    });
                  }
                }}
                className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
              >
                Clear All Data
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Application Settings */}
      <div className="card">
        <div className="flex items-center space-x-3 mb-6">
          <Settings className="h-6 w-6 text-medical-blue" />
          <h2 className="text-xl font-semibold text-gray-900">
            Application Settings
          </h2>
        </div>

        <div className="space-y-4">
          {/* Agent Visibility Toggle */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <h3 className="font-medium text-gray-900 mb-1">
                Floating Agent Assistant
              </h3>
              <p className="text-sm text-gray-600">
                Show or hide the floating AI assistant button for quick actions
                and voice commands
              </p>
            </div>
            <button
              onClick={handleAgentVisibilityToggle}
              className={`
                relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-medical-blue focus:ring-offset-2
                ${isAgentVisible ? "bg-medical-blue" : "bg-gray-300"}
              `}
            >
              <span
                className={`
                  inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ease-in-out
                  ${isAgentVisible ? "translate-x-6" : "translate-x-1"}
                `}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Usage Guidelines */}
      <div className="card bg-medical-lightblue border-l-4 border-medical-blue">
        <h3 className="font-medium text-medical-blue mb-2">Usage Guidelines</h3>
        <div className="text-sm text-gray-700 space-y-1">
          <p>
            • Custom medical dataset items are marked in blue and can be deleted
          </p>
          <p>• Regular export and import your data for backup purposes</p>
          <p>
            • The application works offline and stores data locally in your
            browser
          </p>
          <p>
            • Patient data is kept private and never transmitted to external
            servers
          </p>
          <p>
            • Agent visibility setting is saved and persists across sessions
          </p>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
